<template>
    <div>
        <!-- Champ caché pour la soumission du formulaire HTML classique -->
        <input type="hidden" :name="inputName" :value="selectedId">

        <div class="input-group">
            <!-- Si un emplacement est sélectionné -->
            <div v-if="selectedEmplacement" class="d-flex align-items-center justify-content-between bg-light border border-primary-light px-3" style="min-height: 38px; height: auto; padding-top: 6px; padding-bottom: 6px; width: 100%; border-radius: .25rem; white-space: normal; word-wrap: break-word; flex: 1;">
                <span class="text-dark font-weight-bold" style="line-height: 1.4; word-break: break-word;">
                    <i class="far fa-map-marker-alt text-primary mr-2"></i>
                    {{ selectedEmplacement.full_path || selectedEmplacement.name }}
                    <span v-if="selectedEmplacement.code" class="badge badge-light border ml-2">{{ selectedEmplacement.code }}</span>
                </span>
                <button type="button" class="btn btn-sm p-0 text-danger" @click="clearSelection" title="Effacer la sélection">
                    <i class="fal fa-times-circle h5 mb-0"></i>
                </button>
            </div>

            <!-- Si aucun emplacement n'est sélectionné -->
            <input v-else
                   :id="autocompleteId"
                   type="text"
                   class="form-control"
                   :placeholder="placeholder || 'Rechercher un emplacement (Allée, Étagère, Bac...)'"
                   v-model="searchQuery">

            <!-- Bouton de création rapide -->
            <div v-if="canCreate !== false" class="input-group-append">
                <button class="ix-btn blue" type="button" @click="openCreateModal" title="Créer un nouvel emplacement">
                    <i class="far fa-plus"></i> <span class="d-none d-sm-inline ml-1">Nouveau</span>
                </button>
            </div>
        </div>

        <!-- Modal de création rapide -->
        <div v-if="canCreate !== false" class="modal fade" :id="modalId" tabindex="-1" role="dialog" aria-hidden="true" style="z-index: 1060;">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content border-0 shadow-lg" style="border-radius: 12px;">
                    <div class="modal-header bg-light border-0 py-3 px-4">
                        <h5 class="modal-title font-weight-bold text-dark">
                            <i class="far fa-map-marker-plus text-primary mr-2"></i> Créer un emplacement
                        </h5>
                        <button type="button" class="close" @click="closeCreateModal" aria-label="Close">
                            <i class="fal fa-times"></i>
                        </button>
                    </div>
                    
                    <div class="modal-body px-4 py-4">
                        <!-- Message d'erreur -->
                        <div v-if="errorMessage" class="alert alert-danger mb-3 d-flex align-items-center">
                            <i class="far fa-exclamation-circle mr-2 h5 mb-0"></i> {{ errorMessage }}
                        </div>

                        <div class="form-group">
                            <label class="required">Nom de l'emplacement</label>
                            <input type="text" class="form-control" v-model="form.name" placeholder="Ex: Étagère A, Bac 3, Allée 2...">
                        </div>

                        <div class="form-group">
                            <label class="required">Code d'identification (Scan / QR)</label>
                            <input type="text" class="form-control text-uppercase" v-model="form.code" placeholder="Ex: ETG-A, BAC-3..." required @input="codeManuallyEdited = true">
                            <small class="form-text text-muted">Sert de clé d'identification unique pour les scans et codes-barres.</small>
                        </div>

                        <div class="form-group">
                            <label>Emplacement parent (Optionnel)</label>
                            <select class="form-control" v-model="form.parent_id">
                                <option :value="null">[ Aucun - Niveau racine ]</option>
                                <option v-for="parent in parentOptions" :key="parent.id" :value="parent.id">
                                    {{ parent.full_path || parent.name }}
                                </option>
                            </select>
                        </div>

                        <div class="form-group mb-0">
                            <label>Description (Optionnelle)</label>
                            <textarea class="form-control" v-model="form.description" rows="2" placeholder="Précisez des informations complémentaires..."></textarea>
                        </div>
                    </div>

                    <div class="modal-footer bg-light border-0 py-3 px-4 d-flex justify-content-between">
                        <button type="button" class="ix-btn transparent" @click="closeCreateModal">Annuler</button>
                        <button type="button" class="ix-btn green" @click="submitCreate" :disabled="submitting || !form.name">
                            <span v-if="submitting"><i class="far fa-circle-notch fa-spin mr-2"></i> Enregistrement...</span>
                            <span v-else>Enregistrer & Associer</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import $ from "jquery";

export default {
    name: 'emplacement-selector',
    props: {
        inputName: {
            type: String,
            default: 'emplacement_id'
        },
        initialEmplacement: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: ''
        },
        canCreate: {
            type: Boolean,
            default: true
        },
        excludeId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            selectedEmplacement: null,
            searchQuery: '',
            submitting: false,
            errorMessage: '',
            parentOptions: [],
            emplacements: [],
            codeManuallyEdited: false,
            form: {
                name: '',
                code: '',
                parent_id: null,
                description: ''
            },
            uniqueId: Math.random().toString(36).substr(2, 9)
        };
    },
    computed: {
        selectedId() {
            return this.selectedEmplacement ? this.selectedEmplacement.id : '';
        },
        autocompleteId() {
            return 'emp-autocomplete-' + this.uniqueId;
        },
        modalId() {
            return 'emp-modal-' + this.uniqueId;
        }
    },
    watch: {
        'form.name': function(newVal) {
            this.generateDefaultCode();
        },
        'form.parent_id': function(newVal) {
            this.generateDefaultCode();
        }
    },
    methods: {
        initAutocomplete() {
            this.$nextTick(() => {
                const el = $('#' + this.autocompleteId);
                if (el.length === 0) return;

                el.autocomplete({
                    minLength: 0,
                    source: (request, response) => {
                        $.ajax({
                            url: '/api/emplacements/autocomplete',
                            type: 'get',
                            dataType: 'json',
                            headers: this.getHttpJsonRequestHeaders ? this.getHttpJsonRequestHeaders().headers : {},
                            data: { q: request.term, '!id': this.excludeId },
                            success: (data) => {
                                response($.map(data, (item) => {
                                    return {
                                        id: item.id,
                                        name: item.name,
                                        code: item.code,
                                        full_path: item.full_path,
                                        label: item.full_path || item.name,
                                        value: item.full_path || item.name
                                    };
                                }));
                            }
                        });
                    },
                    select: (e, ui) => {
                        if (itemSelected(ui)) {
                            this.selectEmplacement(ui.item);
                        }
                    }
                }).focus(() => {
                    el.autocomplete("search", '');
                });

                function itemSelected(ui) {
                    return ui && ui.item;
                }
            });
        },
        selectEmplacement(item) {
            this.selectedEmplacement = item;
            this.searchQuery = '';
            this.$emit('change', item);
        },
        clearSelection() {
            this.selectedEmplacement = null;
            this.searchQuery = '';
            this.$emit('change', null);
            this.initAutocomplete();
        },
        generateDefaultCode() {
            if (this.codeManuallyEdited) return;
            
            const name = this.form.name.trim();
            if (!name) {
                this.form.code = '';
                return;
            }
            
            let prefix = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").substring(0, 3).toUpperCase();
            prefix = prefix.replace(/[^A-Z]/g, '');
            if (prefix.length < 3) {
                prefix = (prefix + 'EMP').substring(0, 3);
            }
            
            const parentId = this.form.parent_id;
            const count = this.emplacements.filter(e => e.parent_id === parentId).length;
            
            this.form.code = `${prefix}-${count + 1}`;
        },
        openCreateModal() {
            this.errorMessage = '';
            this.codeManuallyEdited = false;
            this.form = {
                name: '',
                code: '',
                parent_id: null,
                description: ''
            };
            this.loadParentOptions();
            $('#' + this.modalId).modal('show');
        },
        closeCreateModal() {
            $('#' + this.modalId).modal('hide');
        },
        loadParentOptions() {
            fetch('/api/emplacements/tree', {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(response => {
                if (!response.ok) throw new Error("Erreur de chargement");
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    this.emplacements = data.emplacements;
                    this.parentOptions = data.emplacements.map(e => ({
                        id: e.id,
                        name: e.name,
                        full_path: e.full_path,
                        parent_id: e.parent_id,
                        is_active: e.is_active
                    })).sort((a, b) => a.name.localeCompare(b.name));
                }
            })
            .catch(error => {
                console.error("Erreur lors du chargement des emplacements parents", error);
            });
        },
        submitCreate() {
            if (!this.form.name) return;

            this.submitting = true;
            this.errorMessage = '';

            // Récupération dynamique du jeton de sécurité CSRF obligatoire pour les routes web
            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

            fetch('/stocks/emplacements', {
                method: 'POST',
                body: JSON.stringify(this.form),
                ...this.getHttpJsonRequestHeaders(),
                ...{
                    headers: {
                        ...this.getHttpJsonRequestHeaders().headers,
                        'X-CSRF-TOKEN': csrfToken
                    }
                }
            })
            .then(async response => {
                const data = await response.json();
                if (!response.ok) {
                    if (data.errors) {
                        const firstKey = Object.keys(data.errors)[0];
                        throw new Error(data.errors[firstKey][0]);
                    }
                    throw new Error(data.message || "Une erreur est survenue.");
                }
                return data;
            })
            .then(data => {
                this.submitting = false;
                if (data && data.success && data.emplacement) {
                    // Sélectionner automatiquement l'emplacement créé
                    this.selectEmplacement(data.emplacement);
                    this.closeCreateModal();
                    
                    // Déclencher une notification de succès globale
                    if (window.toastr) {
                        window.toastr.success(data.message);
                    }
                } else {
                    this.errorMessage = "Une erreur est survenue lors de la création.";
                }
            })
            .catch(error => {
                this.submitting = false;
                this.errorMessage = error.message || "Une erreur serveur est survenue.";
            });
        }
    },
    mounted() {
        if (this.initialEmplacement) {
            this.selectedEmplacement = this.initialEmplacement;
        } else {
            this.initAutocomplete();
        }
    }
};
</script>

<style scoped>
.font-mono {
    font-family: monospace;
}
.border-primary-light {
    border-color: #cbd5e1 !important;
}
</style>
